import { transliterate } from 'inflected';
import { Func1 } from "@reduxjs/toolkit";

import { NumberRange, SelectItemViewModel } from 'shared/ui-kit';

import { ScreenerEtfBackendModel } from "./services/model";
import { ScreenerStoreFilter, ScreenerStoreFilterNumberRangeInput, ScreenerStoreFilterSelectInput } from "./redux/model";

export interface FieldConfiguration {
	systemName: string;
	title: string;
	createDefaultInput(etfs: ScreenerEtfBackendModel[], currency: string): ScreenerStoreFilterSelectInput | ScreenerStoreFilterNumberRangeInput;

	tryDescribeSelectedValue(filter: ScreenerStoreFilter): string | null;

	hasSelectedValue(filter: ScreenerStoreFilter): boolean;

	serializeValue(input: ScreenerStoreFilterSelectInput | ScreenerStoreFilterNumberRangeInput, result: { [key: string]: string }): void;
	deserializeValue(input: ScreenerStoreFilterSelectInput | ScreenerStoreFilterNumberRangeInput, result: { [key: string]: string }): void;

	isAcceptable(filter: ScreenerStoreFilter, etf: ScreenerEtfBackendModel, currency: string): boolean;
}
export interface SelectFieldConfiguration extends FieldConfiguration {
}
export interface NumberRangeFieldConfiguration extends FieldConfiguration {
	formatter: wNumb.Instance
}

export const KnownFields = {
	assetClass: createSelectFieldConfiguration({
		systemName: 'assetClass',
		title: 'Актив',
		getValueFromEtf: etf => etf.tags.investmentPolicy.assetClass
	}),
	assetSubClass: createSelectFieldConfiguration({
		systemName: 'assetSubClass',
		title: 'Категория',
		getValueFromEtf: etf => etf.tags.investmentPolicy.assetSubClass
	}),
	region: createSelectFieldConfiguration({
		systemName: 'region',
		title: 'Регион',
		getValueFromEtf: etf => etf.tags.geography.area
	}),
	county: createSelectFieldConfiguration({
		systemName: 'county',
		title: 'Страна',
		getValueFromEtf: etf => etf.tags.geography.country
	}),
	marketType: createSelectFieldConfiguration({
		systemName: 'marketType',
		title: 'Рынок',
		getValueFromEtf: etf => etf.tags.geography.marketType
	}),
	issuer: createSelectFieldConfiguration({
		systemName: 'issuer',
		title: 'Управляющая компания',
		getValueFromEtf: etf => etf.issuer.name
	}),
	tradingStatus: createSelectFieldConfiguration({
		systemName: 'tradingStatus',
		title: 'Торговый статус фонда',
		getValueFromEtf: etf => etf.tradeStatus?.name || '-',
		etfComparer: (etf1, etf2) => (etf1.tradeStatus?.sortOrder || 0) - (etf2.tradeStatus?.sortOrder || 0)
	}),
	currency: createSelectFieldConfiguration({
		systemName: 'currency',
		title: 'Отчетная валюта',
		getValueFromEtf: etf => etf.currency.name
	}),
	dividendPolicy: createSelectFieldConfiguration({
		systemName: 'dividendPolicy',
		title: 'Политика дивидендов',
		getValueFromEtf: etf => etf.tags.investmentPolicy.dividendPolicy
	}),

	assetsUnderManagement: createNumberRangeFieldConfiguration({
		systemName: 'assetsUnderManagement',
		title: 'СЧА',
		formatter: NumberRange.decimalsFormatter,
		getValueFromEtf: (etf, currency) => etf.assetsUnderManagementByCurrency[currency]
	}),
	commisionPercent: createNumberRangeFieldConfiguration({
		systemName: 'commisionPercent',
		title: 'Комисcия',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: etf => etf.commisionPercent * 100
	}),

	return1Day: createNumberRangeFieldConfiguration({
		systemName: 'return1Day',
		title: 'За день',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: (etf, currency) => mul(etf.fieldValuesByCurrency[currency]['metric/return/1day'], 100)
	}),
	return1Week: createNumberRangeFieldConfiguration({
		systemName: 'return1Week',
		title: 'За неделю',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: (etf, currency) => mul(etf.fieldValuesByCurrency[currency]['metric/return/1week'], 100)
	}),
	return1Month: createNumberRangeFieldConfiguration({
		systemName: 'return1Month',
		title: 'За месяц',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: (etf, currency) => mul(etf.fieldValuesByCurrency[currency]['metric/return/1month'], 100)
	}),
	return3Months: createNumberRangeFieldConfiguration({
		systemName: 'return3Months',
		title: 'За квартал',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: (etf, currency) => mul(etf.fieldValuesByCurrency[currency]['metric/return/3month'], 100)
	}),
	return1Year: createNumberRangeFieldConfiguration({
		systemName: 'return1Year',
		title: 'За год',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: (etf, currency) => mul(etf.fieldValuesByCurrency[currency]['metric/return/1year'], 100)
	}),

	structureTotal: createNumberRangeFieldConfiguration({
		systemName: 'structureTotal',
		title: 'Количество',
		formatter: NumberRange.defaultFormatter,
		getValueFromEtf: etf => etf.assets.totalCount
	}),
	structureTop3Percent: createNumberRangeFieldConfiguration({
		systemName: 'structureTop3Percent',
		title: 'Топ 3%',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: etf => etf.assets.top3Percent
	}),
	structureTop5Percent: createNumberRangeFieldConfiguration({
		systemName: 'structureTop5Percent',
		title: 'Топ 5%',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: etf => etf.assets.top5Percent
	}),
	structureTop10Percent: createNumberRangeFieldConfiguration({
		systemName: 'structureTop10',
		title: 'Топ 10%',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: etf => etf.assets.top10Percent
	}),
	structureTop50Percent: createNumberRangeFieldConfiguration({
		systemName: 'structureTop50',
		title: 'Топ 50%',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: etf => etf.assets.top50Percent
	}),


	sharpe: createNumberRangeFieldConfiguration({
		systemName: 'sharpe',
		title: 'Шарп',
		formatter: NumberRange.decimalsFormatter,
		getValueFromEtf: (etf, currency) => etf.fieldValuesByCurrency[currency]['metric/sharpe']
	}),
	sortino: createNumberRangeFieldConfiguration({
		systemName: 'sortino',
		title: 'Сортино',
		formatter: NumberRange.decimalsFormatter,
		getValueFromEtf: (etf, currency) => etf.fieldValuesByCurrency[currency]['metric/sortino']
	}),
	var5Percent: createNumberRangeFieldConfiguration({
		systemName: 'var5Percent',
		title: 'VaR 5%',
		formatter: NumberRange.percentFormatter,
		getValueFromEtf: (etf, currency) => {
			const value = etf.fieldValuesByCurrency[currency]['metric/var'];
			return value == null ? null : value * 100;
		}
	})
};

export const allFieldConfiguration: FieldConfiguration[] = Object.values(KnownFields);

interface createSelectFieldConfigurationParams {
	systemName: string;
	title: string;

	getValueFromEtf: (etf: ScreenerEtfBackendModel) => string;
	etfComparer?: (item1: ScreenerEtfBackendModel, item2: ScreenerEtfBackendModel) => number;
}
function createSelectFieldConfiguration(params: createSelectFieldConfigurationParams): SelectFieldConfiguration {
	return {
		systemName: params.systemName,
		title: params.title,
		createDefaultInput(etfs: ScreenerEtfBackendModel[]): ScreenerStoreFilterSelectInput {
			const sortedEtfs = params.etfComparer
				? [...etfs].sort(params.etfComparer)
				: etfs;

			const items = calculateSelectItems(sortedEtfs, params.getValueFromEtf);

			return {
				items: items,
				selectedKey: ''
			}
		},

		tryDescribeSelectedValue(filter: ScreenerStoreFilter): string | null {
			const input = filter.inputs[params.systemName] as ScreenerStoreFilterSelectInput;
			if (input.selectedKey === '')
				return null;

			return input.items.find(x => x.key == input.selectedKey)?.text || null;
		},

		isAcceptable(filter: ScreenerStoreFilter, etf: ScreenerEtfBackendModel): boolean {
			const input = filter.inputs[params.systemName] as ScreenerStoreFilterSelectInput;
			if (input.selectedKey === '')
				return true;

			return input.selectedKey == transliterate(params.getValueFromEtf(etf));
		},

		hasSelectedValue(filter: ScreenerStoreFilter): boolean {
			const input = filter.inputs[params.systemName] as ScreenerStoreFilterSelectInput;
			return input.selectedKey !== '';
		},

		serializeValue(input: ScreenerStoreFilterSelectInput, result: { [key: string]: string }): void {
			if (!!input.selectedKey)
				result[params.systemName] = input.selectedKey;
		},
		deserializeValue(input: ScreenerStoreFilterSelectInput, result: { [key: string]: string }): void {
			const serializedValue = result[params.systemName];
			if (!!serializedValue)
				input.selectedKey = serializedValue;
		}
	}
}

interface createNumberRangeFieldConfigurationParams {
	systemName: string;
	title: string;

	formatter: wNumb.Instance;

	getValueFromEtf: (etf: ScreenerEtfBackendModel, currency: string) => number | null;
}
function createNumberRangeFieldConfiguration(params: createNumberRangeFieldConfigurationParams): NumberRangeFieldConfiguration {
	return {
		systemName: params.systemName,
		title: params.title,
		formatter: params.formatter,
		createDefaultInput(etfs: ScreenerEtfBackendModel[], currency: string): ScreenerStoreFilterNumberRangeInput {
			const restriction = calculateRangeRestrictions(etfs, etf => params.getValueFromEtf(etf, currency));
			return {
				restriction: restriction,
				value: restriction
			};
		},
		tryDescribeSelectedValue(_: ScreenerStoreFilter): string | null {
			return null;
		},

		isAcceptable(filter: ScreenerStoreFilter, etf: ScreenerEtfBackendModel, currency: string): boolean {
			const value = params.getValueFromEtf(etf, currency);

			var input = filter.inputs[params.systemName] as ScreenerStoreFilterNumberRangeInput;
			if (input.value.min === input.restriction.min && input.value.max === input.restriction.max)
				return true;

			if (value === null)
				return false;

			return input.value.min <= value && value <= input.value.max;
		},

		hasSelectedValue(filter: ScreenerStoreFilter): boolean {
			var input = filter.inputs[params.systemName] as ScreenerStoreFilterNumberRangeInput;
			return input.value.min !== input.restriction.min || input.value.max !== input.restriction.max;
		},

		serializeValue(input: ScreenerStoreFilterNumberRangeInput, result: { [key: string]: string }): void {
			if (input.value.min !== input.restriction.min)
				result[`${params.systemName}.min`] = input.value.min.toString();
			if (input.value.max !== input.restriction.max)
				result[`${params.systemName}.max`] = input.value.max.toString();
		},
		deserializeValue(input: ScreenerStoreFilterNumberRangeInput, result: { [key: string]: string }): void {
			const resultValue = { ...input.value };

			const serializedMinValue = result[`${params.systemName}.min`];
			if (!!serializedMinValue)
				resultValue.min = parseFloat(serializedMinValue);

			const serializedMmaxValue = result[`${params.systemName}.max`];
			if (!!serializedMmaxValue)
				resultValue.max = parseFloat(serializedMmaxValue);

			input.value = resultValue;
		}
	}
}

function calculateSelectItems(etfs: ScreenerEtfBackendModel[], getTag: Func1<ScreenerEtfBackendModel, string>): SelectItemViewModel[] {
	return etfs.reduce(
		(memo, etf) => {
			const tag = getTag(etf);
			const transliteratedTag = transliterate(tag)

			const foundedSelectItem = memo.find(r => r.key === transliteratedTag);
			if (!foundedSelectItem) {
				memo.push({ key: transliteratedTag, text: tag });
			}

			return memo;
		},
		<SelectItemViewModel[]>[{ key: '', text: '' }]);
}
function calculateRangeRestrictions(etfs: ScreenerEtfBackendModel[], getValue: Func1<ScreenerEtfBackendModel, number | null>): NumberRange {
	const values: number[] = etfs.map(etf => getValue(etf)).filter(val => val != null).filter(val => isFinite(val!)).map(x => x!);
	const min = Math.round(Math.min(...values) * 100) / 100;
	const max = Math.round(Math.max(...values) * 100) / 100;

	return { min, max };
}


function mul(val: number | null, multiplier: number) {
	return val == null
		? null
		: val * multiplier
}